// Our own divider between the two split-screen panes — #1415.
//
// Apryse renders one of its own (`[data-element="compareResizeBar"]`, MultiViewer.js) but its drag
// arithmetic cannot be corrected from outside: `ResizeBar.js` maps the pointer to
// `clientX - getWebViewerRect().left`, and in web-component mode that rect is the WHOLE viewer's
// host node — so every column left of pane 1 (the `Elevation references` sidebar, ~310px) is
// counted into pane 1's width — and `MultiViewer.js` then makes the two pane widths sum to the
// entire row, over-committing it by the divider plus our 240px middle dock. Its `MIN_WIDTH = 350`
// is clamped twice on top of that, so once the row is narrower than 700px the drag is a no-op. The
// vendor bundle is not patched (out of scope), so Apryse's divider is hidden by the stylesheet and
// this node takes its place; nothing routes through Apryse's `onResize` any more.
//
// The pattern is `middlePanel.ts`'s, for the same reason: the node is OURS, created and appended by
// us, so React never wipes it. `#container1` / `#container2` / `.MultiViewer` are Apryse
// React-owned and lose any attribute, class or inline style we write onto them on their next
// render — a recorded bug in this repo. Nothing here writes to them.
//
// It is appended LAST (the safest place to add a child to a list React owns) and `order` paints it
// between the left pane and the middle dock; the layout rules live in
// public/webviewer-quotation.css alongside the rest of the split-screen chrome, since they style
// Apryse's elements (pane flex, order) as much as ours.

import type { TSplitRowGeometry } from "../interactions/paneDivider.helpers"
import { resolveSplitGrow } from "../interactions/paneDivider.helpers"

// Apryse's own root for the side-by-side layout (MultiViewer.js); not configurable.
const MULTI_VIEWER_CLASS = "MultiViewer"
const MULTI_VIEWER_SELECTOR = `.${MULTI_VIEWER_CLASS}`
// Apryse's class on each pane container (`#container1` / `#container2`), used to tell the panes
// apart from every other flex child of the row when measuring.
const PANE_CLASS = "CompareContainer"
// Apryse's id on the LEFT pane. The panes are placed by `order`, so DOM order is not the visual one
// — the id is, and pane 1's own box inset is the one thing the arithmetic must attribute to a side.
const PRIMARY_PANE_ID = "container1"

export const PANE_DIVIDER_CLASS = "asq-pane-divider"
// Set for the duration of a drag: keeps the col-resize cursor and the pressed handle treatment
// while the pointer is captured and has left the 14px column.
export const PANE_DIVIDER_DRAGGING_CLASS = "asq-pane-divider--dragging"

// The two `flex-grow` values the panes are sized from (see the stylesheet). They are written onto
// the web component's HOST element — an element we may touch (it is not one of the three
// React-owned nodes above, and nothing in Apryse's UI writes inline styles to it) — and reach
// `.MultiViewer`'s children by inheritance, which is how a value can be published to a node we are
// not allowed to write to.
const SPLIT_GROW_PRIMARY_PROPERTY = "--asq-split-grow-primary"
const SPLIT_GROW_SPLIT_PROPERTY = "--asq-split-grow-split"

// The divider's grab column. Unchanged from the width Apryse's own bar had (#1415 is a behaviour
// fix, not a visual one) and mirrored in the stylesheet's `flex-basis`; the arithmetic reads it
// from the live element, this is only the fallback for a not-yet-laid-out node.
export const PANE_DIVIDER_WIDTH = 14

export type TPaneDividerHost = {
	// Stable across attach/detach cycles, so listeners bound to it survive them.
	element: HTMLElement
	// (Re)parent the divider into the split-screen root that exists NOW, or detach it when split
	// screen is closed. Idempotent, and re-run on every `layoutEpoch` bump — Apryse re-mounts the
	// document area around `.MultiViewer`. Returns whether the divider is currently attached.
	sync: (options: { shadowRoot: ShadowRoot | null | undefined; isSplitActive: boolean }) => boolean
	// Measure the flex row the panes share, from the elements that exist right now. Null when the
	// row is not laid out (split screen closed, or the divider not attached yet). Call it per
	// pointermove rather than once per gesture: it reads the row and never a pane's share, so its
	// output is invariant under the drag itself — but the row is not (a panel still animating open
	// changes it mid-gesture).
	measure: (minPaneWidth: number) => TSplitRowGeometry | null
	// Publish pane 1's share of the pane space. Cheap enough to call on every pointermove: it sets
	// two custom properties and lets the browser re-lay-out the flex row, with no JS measurement.
	publishFraction: (fraction: number) => void
	destroy: () => void
}

// Resolve the row's left content edge and content width. `.MultiViewer` carries neither padding nor
// a border today, but reading them keeps the arithmetic correct if it ever does — the flex line is
// laid out in the CONTENT box, and that is what the pointer must be measured against.
const measureRowBox = (root: HTMLElement): { rowLeft: number; rowWidth: number } => {
	const rect = root.getBoundingClientRect()
	const style = window.getComputedStyle(root)
	const insetStart = (parseFloat(style.borderLeftWidth) || 0) + (parseFloat(style.paddingLeft) || 0)
	const insetEnd = (parseFloat(style.borderRightWidth) || 0) + (parseFloat(style.paddingRight) || 0)
	return { rowLeft: rect.left + insetStart, rowWidth: Math.max(0, rect.width - insetStart - insetEnd) }
}

// An element's horizontal border + padding — the part of its box that is NOT the flex content it
// grows into. Read live, never hardcoded: Apryse's own `MultiViewer.scss` puts a 2px border on each
// pane (that border IS the line between the two panes, see webviewer-quotation.css) and it writes
// an inline `padding: 16px` onto a pane whose document has not loaded yet, so the number is neither
// constant nor ours to assume.
const measureHorizontalInset = (element: Element): number => {
	const style = window.getComputedStyle(element)
	return (
		(parseFloat(style.borderLeftWidth) || 0) +
		(parseFloat(style.paddingLeft) || 0) +
		(parseFloat(style.paddingRight) || 0) +
		(parseFloat(style.borderRightWidth) || 0)
	)
}

export const createPaneDividerHost = (): TPaneDividerHost => {
	const element = document.createElement("div")
	element.className = PANE_DIVIDER_CLASS
	// Announced as what it is; the drag itself is pointer-driven (see interactions/usePaneDivider).
	element.setAttribute("role", "separator")
	element.setAttribute("aria-orientation", "vertical")
	element.setAttribute("aria-label", "Resize the split view")

	const detach = () => element.remove()

	// Remembered from the last `sync`, so the split can still be published (reset to an even one, in
	// practice) while the divider is detached between split-screen sessions.
	let lastShadowRoot: ShadowRoot | null = null

	const resolveRoot = (): HTMLElement | null => {
		const parent = element.parentElement
		return parent?.classList.contains(MULTI_VIEWER_CLASS) ? parent : null
	}

	// The web component's host element, reached from the node itself so callers need not thread the
	// shadow root through.
	const resolveStyleTarget = (): HTMLElement | null => {
		const root = element.getRootNode()
		if (root instanceof ShadowRoot) return root.host as HTMLElement
		return (lastShadowRoot?.host as HTMLElement | undefined) ?? null
	}

	const sync: TPaneDividerHost["sync"] = ({ shadowRoot, isSplitActive }) => {
		if (shadowRoot) lastShadowRoot = shadowRoot
		const root = isSplitActive ? (shadowRoot?.querySelector(MULTI_VIEWER_SELECTOR) as HTMLElement | null) : null
		if (!root?.isConnected) {
			detach()
			return false
		}
		// Always re-append rather than insert: `order` owns the visual placement, so the DOM position
		// only has to be somewhere React will not trip over.
		if (element.parentElement !== root) root.appendChild(element)
		return true
	}

	const measure: TPaneDividerHost["measure"] = (minPaneWidth) => {
		const root = resolveRoot()
		if (!root?.isConnected) return null
		const { rowLeft, rowWidth } = measureRowBox(root)
		if (rowWidth <= 0) return null
		const dividerWidth = element.offsetWidth || PANE_DIVIDER_WIDTH
		// Everything else seated in the row: today that is the middle `Object` dock, and nothing
		// else — Apryse's own divider is `display: none` and so measures 0. Summing whatever is
		// actually IN FLOW means a future flex sibling is paid for automatically instead of silently
		// stealing the drag's frame of reference (which is the mistake Apryse's own bar makes).
		// Out-of-flow children are skipped: Apryse's `CompareZoomOverlay` renders its flyout menus
		// here as absolutely-positioned popups, which have a width but claim no room in the row.
		let reservedWidth = 0
		// Pane 1's own border + padding sits in front of the space it grows into, so it offsets the
		// divider by a constant. Attributed to `rowLeft` below rather than to the shared pane space.
		let primaryPaneInset: number | null = null
		let firstPaneInset: number | null = null
		for (const child of Array.from(root.children)) {
			if (child === element) continue
			const width = (child as HTMLElement).offsetWidth || 0
			if (width <= 0) continue
			// A pane's BORDER AND PADDING is reserved too, even though its content is not.
			//
			// Each pane is `flex: <grow> 1 0px`, and a flex item's used base size cannot shrink its
			// content box below zero — so every pane claims its own border + padding before the two
			// grows divide anything, and the free space they actually share is the pane space MINUS
			// those insets. Ignoring them made the divider travel at `(paneSpace − 8) / paneSpace` of
			// the pointer: 0.9944 measured, harmless at 1440px (3px off over a full drag) but ~4.7px
			// at 1920px, past AC1's tolerance. It is measured, not subtracted as a constant 8, because
			// the value is Apryse's (see `measureHorizontalInset`).
			if (child.classList.contains(PANE_CLASS)) {
				const inset = measureHorizontalInset(child)
				reservedWidth += inset
				if (firstPaneInset === null) firstPaneInset = inset
				if (child.id === PRIMARY_PANE_ID) primaryPaneInset = inset
				continue
			}
			// Everything else seated in the row: today that is the middle `Object` dock, and nothing
			// else — Apryse's own divider is `display: none` and so measures 0. Summing whatever is
			// actually IN FLOW means a future flex sibling is paid for automatically instead of silently
			// stealing the drag's frame of reference (which is the mistake Apryse's own bar makes).
			// Out-of-flow children are skipped: Apryse's `CompareZoomOverlay` renders its flyout menus
			// here as absolutely-positioned popups, which have a width but claim no room in the row.
			const { position } = window.getComputedStyle(child)
			if (position === "absolute" || position === "fixed") continue
			reservedWidth += width
		}
		// `rowLeft` is where pane 1's SHARE begins, which is its border box's left edge plus its own
		// inset — the whole inset, since the pane's right border and padding also sit in front of the
		// divider. Without this the corrected scale above would still leave a constant ~4px offset.
		// Falls back to the first pane found when the id is not Apryse's (a future rename): the panes
		// are symmetric today, so either inset is the same number.
		const paneInset = primaryPaneInset ?? firstPaneInset ?? 0
		return { rowLeft: rowLeft + paneInset, rowWidth, dividerWidth, reservedWidth, minPaneWidth }
	}

	const publishFraction: TPaneDividerHost["publishFraction"] = (fraction) => {
		const target = resolveStyleTarget()
		if (!target) return
		const { primaryGrow, splitGrow } = resolveSplitGrow(fraction)
		target.style.setProperty(SPLIT_GROW_PRIMARY_PROPERTY, `${primaryGrow}`)
		target.style.setProperty(SPLIT_GROW_SPLIT_PROPERTY, `${splitGrow}`)
	}

	return { element, sync, measure, publishFraction, destroy: detach }
}
